import React, { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import { useI18nStore } from "../../stores/i18nStore";
import { useAuthStore } from "../../stores/authStore";
import { toast } from "react-hot-toast";
import apiClient from "../../services/api/apiClient";

interface DeleteAccountSectionProps {
  onDeleteSuccess?: () => void;
}

export const DeleteAccountSection: React.FC<DeleteAccountSectionProps> = ({
  onDeleteSuccess,
}) => {
  const { t } = useI18nStore();
  const { logout } = useAuthStore();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [password, setPassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirmationText !== "DELETE MY ACCOUNT") {
      toast.error(t.deleteAccountConfirmationError);
      return;
    }

    if (!password) {
      toast.error(t.passwordRequired);
      return;
    }

    setIsDeleting(true);

    try {
      const response = await apiClient.delete("/account", {
        data: {
          confirmationText,
          password,
        },
      });

      if (response.data.success) {
        toast.success(t.accountDeletedSuccessfully);

        // Logout and redirect after short delay
        setTimeout(async () => {
          // Awaited: the page load below would cut the clearing short.
          await logout();
          onDeleteSuccess?.();
          window.location.href = "/";
        }, 2000);
      } else {
        throw new Error(response.data.error?.message || "Deletion failed");
      }
    } catch (error: any) {
      console.error("Account deletion error:", error);

      // 400: the confirmation; 502: the subscription could not be cancelled,
      // so nothing was deleted. Both carry a message meant for the user.
      if (error.response?.status === 400 || error.response?.status === 502) {
        toast.error(error.response.data.error?.message || t.deleteAccountError);
      } else if (error.response?.status === 401) {
        toast.error(t.invalidPassword);
      } else {
        toast.error(t.deleteAccountError);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="border-t border-gray-200 pt-8 mt-8">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              {t.dangerZone}
            </h3>
            <p className="text-sm text-red-700 mb-4">
              {t.deleteAccountWarning}
            </p>

            {!showConfirmation ? (
              <button
                onClick={() => setShowConfirmation(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>{t.deleteAccount}</span>
              </button>
            ) : (
              <div className="space-y-4">
                <div className="bg-white border border-red-300 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-3 font-medium">
                    {t.deleteAccountConfirmationPrompt}
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.typeToConfirm}:{" "}
                        <span className="font-mono text-red-600">
                          DELETE MY ACCOUNT
                        </span>
                      </label>
                      <input
                        type="text"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="DELETE MY ACCOUNT"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.password}
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder={t.enterYourPassword}
                      />
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-800">
                      <strong>{t.warning}:</strong>{" "}
                      {t.deleteAccountWarningDetails}
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleDeleteAccount}
                    disabled={
                      isDeleting ||
                      confirmationText !== "DELETE MY ACCOUNT" ||
                      !password
                    }
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>{isDeleting ? t.deleting : t.confirmDeletion}</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowConfirmation(false);
                      setConfirmationText("");
                      setPassword("");
                    }}
                    disabled={isDeleting}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
                  >
                    {t.cancel}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
